import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { FiMenu, FiX, FiShoppingCart, FiFileText, FiGlobe, FiChevronDown, FiSearch } from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { tracker } from '../utils/tracker';
import SearchModal from './SearchModal';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [prodDropdown, setProdDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { cartCount } = useCart();
  const { lang, toggleLang, t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`header-v2 ${scrolled ? 'header-v2-scrolled' : ''}`}>
      <div className="header-v2-top">
        <div className="header-v2-top-inner">
          <span className="header-v2-tagline">{t('header.tagline')}</span>
        </div>
      </div>

      <div className="header-v2-main">
        <Link to="/" className="header-v2-logo">
          <img src="/images/logo-cp-trans-sm.png" alt="Cellophane & Paper - Home" className="header-v2-logo-img" width="160" height="160" />
        </Link>

        <nav className={`header-v2-nav ${mobileOpen ? 'open' : ''}`} aria-label="Navigazione principale">
          <NavLink to="/chi-siamo" className="header-v2-link" onClick={() => setMobileOpen(false)}>
            {t('header.about')}
          </NavLink>
          <div
            className="header-v2-dropdown"
            onMouseEnter={() => setProdDropdown(true)}
            onMouseLeave={() => setProdDropdown(false)}
          >
            <NavLink to="/prodotti" className="header-v2-link" onClick={() => setMobileOpen(false)}>
              {t('header.catalog')} <FiChevronDown size={14} aria-hidden="true" />
            </NavLink>
            {prodDropdown && (
              <div className="header-v2-dropdown-menu" role="menu">
                {[
                  { to: '/prodotti/alimentare', it: 'Settore Alimentare', en: 'Food Sector' },
                  { to: '/prodotti/macelleria', it: 'Linea Macelleria', en: 'Butcher Line' },
                  { to: '/prodotti/domestica', it: 'Professionale & Domestica', en: 'Pro & Home' },
                  { to: '/prodotti/confezionamento', it: 'Confezionamento', en: 'Packaging' },
                ].map(item => (
                  <Link key={item.to} to={item.to} role="menuitem" onClick={() => { setMobileOpen(false); setProdDropdown(false); }}>
                    {lang === 'en' ? item.en : item.it}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <NavLink to="/certificazioni" className="header-v2-link" onClick={() => setMobileOpen(false)}>
            {t('header.certifications')}
          </NavLink>
          <NavLink to="/news" className="header-v2-link" onClick={() => setMobileOpen(false)}>
            {t('header.news')}
          </NavLink>
          <NavLink to="/contatti#contact-form" className="header-v2-link header-v2-cta" onClick={() => setMobileOpen(false)}>
            {t('header.quote')}
          </NavLink>
        </nav>

        <div className="header-v2-actions">
          <button
            onClick={toggleLang}
            className="header-v2-lang"
            aria-label={lang === 'it' ? 'Switch to English' : "Passa all'italiano"}
          >
            <FiGlobe size={15} aria-hidden="true" />
            {lang === 'it' ? 'EN' : 'IT'}
          </button>
          <button
            onClick={() => setSearchOpen(true)}
            className="header-v2-search"
            aria-label={lang === 'en' ? 'Search products' : 'Cerca prodotti'}
          >
            <FiSearch size={18} aria-hidden="true" />
          </button>
          <a href="/catalogo-cp-2026.pdf" target="_blank" rel="noopener noreferrer" className="header-v2-pdf" aria-label="Scarica catalogo PDF" onClick={() => tracker.event('catalog_download', 'engagement', { source: 'header' })}>
            <FiFileText size={17} aria-hidden="true" />
          </a>
          <Link to="/carrello" className="header-v2-cart" aria-label={`Carrello${cartCount > 0 ? `, ${cartCount} prodotti` : ''}`}>
            <FiShoppingCart size={20} aria-hidden="true" />
            {cartCount > 0 && <span className="header-v2-cart-badge" aria-hidden="true" key={cartCount}>{cartCount}</span>}
          </Link>
          <button className="header-v2-burger" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? 'Chiudi menu' : 'Apri menu'} aria-expanded={mobileOpen}>
            {mobileOpen ? <FiX size={22} aria-hidden="true" /> : <FiMenu size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};

export default Header;
